import React from 'react'
import './styles/notification-test-builder.scss'
import { Well } from '@pushly/aqe/lib/components'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, Skeleton } from 'antd'
import { NotificationTestConfigurationModel } from '../../../models/notification/notification-test-configuration.model'
import { getClassNames } from '../../../_utils/classnames'
import { useInputState } from '../../../_utils/use-input-state'
import { getComputedCancelText, getComputedSubmitText, getSubmitProps } from '../helpers'
import { INotificationBuilderState } from '../interfaces/notification-builder-state.interface'
import { NotificationBuilderLevel, NotificationBuilderTheme } from '../enums'
import { useService } from '../../../hooks/use-service'
import { AppState } from '../../../stores/app'

interface INotificationTestBuilder {
    builder: INotificationBuilderState
    value: NotificationTestConfigurationModel | undefined
    onChange: (value: NotificationTestConfigurationModel | null) => any
    loading?: boolean
    disabled?: boolean
    toggleDisabled?: boolean
    toggleDisabledReason?: string
    //
    isDraft?: boolean
    isTemplate?: boolean
    //
    hideSubmit?: boolean
    submitText?: string
    cancelText?: string
    onSubmit?: (isDraft?: boolean) => void | Promise<void>
    onCancel?: () => void | Promise<void>
}

const NotificationTestBuilder = (props: INotificationTestBuilder) => {
    const { currentUser, currentDomain } = useService<AppState>(AppState)

    const {
        loading,
        builder,
        disabled,
        value,
        isDraft,
        isTemplate,
        hideSubmit = true,
        submitText: submitTextOverride,
        cancelText: cancelTextOverride,
        onCancel,
        onSubmit,
        onChange,
    } = props

    const workingModel: NotificationTestConfigurationModel = !!value
        ? value.clone()
        : NotificationTestConfigurationModel.build({})

    const [nameValue, setNameValue] = useInputState((name) => {
        const update = workingModel.clone()
        update.setName(name)
        onChange(update)
    }, 150)

    const variant = builder.variants[builder.selectedVariantIdx]
    const meta = variant.getDisplayMeta()

    const isMobileTheme = builder.theme === NotificationBuilderTheme.MOBILE
    const isDomainLevel = builder.level === NotificationBuilderLevel.DOMAIN
    const isNewNotification = !Object.keys(meta).length
    const isScheduledNotification = !isNewNotification && !meta.isDraft && !meta.notEditable

    let submitActions: Well['props']['submitActions']
    if (!isMobileTheme && !isTemplate && (isNewNotification || !isScheduledNotification) && isDomainLevel) {
        submitActions = [
            {
                text: meta.isDraft ? 'Update Draft ' : 'Save as Draft',
                onClick: () => onSubmit?.(true),
            },
        ]
    }

    /**
     * ***
     * all internal user roles are prohibited from sending on non-demo|internal domains
     * ***
     */
    const submitAllowed =
        !currentDomain?.isPreviewDomain &&
        (!currentUser?.isInternalUser || currentDomain?.isDemoDomain || currentDomain?.isInternal)

    const submitProps = getSubmitProps({
        hide: hideSubmit,
        disabled: !submitAllowed,
        onSubmit,
        onCancel,
        submitText: getComputedSubmitText({
            formMode: builder.mode,
            formType: isTemplate ? 'named-template' : 'send',
            variants: builder.variants,
            override: submitTextOverride,
        }),
        cancelText: getComputedCancelText(cancelTextOverride),
        submitActions,
    })

    return (
        <Well className={getClassNames('notification-test-builder', 'nested')} title="A/B Testing" {...submitProps}>
            <Skeleton loading={loading} active={true} avatar={false} title={false}>
                <Well mode="ghost" hideHeader={true} hideFooter={true}>
                    <Form.Item label="Name">
                        <Input
                            size="middle"
                            disabled={disabled}
                            value={nameValue ?? workingModel.getName()}
                            onChange={(ev) => {
                                const v = ev.target.value
                                setNameValue(v)
                            }}
                        />
                    </Form.Item>
                </Well>
            </Skeleton>
        </Well>
    )
}

export default NotificationTestBuilder
